/**
 * The tools at work: a stroke begins on a press (mouse, or Space at the keyboard cursor), follows
 * the pointer, and ends as one undo step. Left paints the selected element, right paints dirt.
 */

import type { KitDoc } from '../model/doc';
import type { Point } from '../model/grid';
import { cellAt, paint } from '../model/grid';
import { kitElement } from '../model/palette';
import type { ToolId } from '../model/shapes';
import { floodPoints, isShapeTool, linePoints, shapePoints, toolInfo } from '../model/shapes';
import type { KitState } from './state';

export interface Stroke {
  readonly tool: ToolId;
  readonly ch: string;
  readonly before: KitDoc;
  readonly anchor: Point;
  last: Point;
  /** Started with the keyboard: Space again finishes it. */
  readonly keyboard: boolean;
}

export interface Tools {
  tool: ToolId;
  element: string;
  /** Picked with the eyedropper: select it and go back to the tool before. */
  picked(ch: string): void;
}

export class Painter {
  stroke: Stroke | null = null;

  constructor(private readonly st: KitState, private readonly tools: Tools) {}

  /** A press at cell `p`: button 0 paints the element, 2 paints dirt. */
  begin(p: Point, button: number, keyboard = false): void {
    this.cancel();
    const tool = this.tools.tool;
    const ch = button === 2 ? '.' : this.tools.element;
    if (tool === 'picker') {
      const got = cellAt(this.st.doc.grid, p.x, p.y);
      if (got !== undefined && kitElement(got)) this.tools.picked(got);
      return;
    }
    if (tool === 'flood') {
      const before = this.st.doc;
      this.st.edit({ ...before, grid: paint(before.grid, floodPoints(before.grid, p), ch) });
      return;
    }
    this.stroke = { tool, ch, before: this.st.doc, anchor: p, last: p, keyboard };
    if (tool === 'pencil') this.paintTo(p);
    else if (keyboard) this.st.say(`${toolInfo(tool).label} FROM ${p.x},${p.y}: MOVE, SPACE TO FINISH, ESC TO CANCEL`);
  }

  /** The pointer (or keyboard cursor) is over cell `p` now. */
  move(p: Point): void {
    const s = this.stroke;
    if (!s || (s.last.x === p.x && s.last.y === p.y)) return;
    if (s.tool === 'pencil') this.paintTo(p);
    s.last = p;
  }

  private paintTo(p: Point): void {
    const s = this.stroke;
    if (!s) return;
    const doc = this.st.doc;
    this.st.set({ ...doc, grid: paint(doc.grid, linePoints(s.last, p), s.ch) });
  }

  /** Release: a shape is drawn now; either way the stroke becomes one undo step. */
  end(): void {
    const s = this.stroke;
    if (!s) return;
    this.stroke = null;
    if (isShapeTool(s.tool)) {
      const doc = this.st.doc;
      this.st.set({ ...doc, grid: paint(doc.grid, shapePoints(s.tool, s.anchor, s.last), s.ch) });
    }
    this.st.commit(s.before);
  }

  /** Drop a shape in progress (a pencil stroke keeps what it painted). */
  cancel(): void {
    const s = this.stroke;
    if (!s) return;
    if (isShapeTool(s.tool)) this.stroke = null;
    else this.end();
  }

  /** Space at the keyboard cursor: use the tool once, or start / finish a shape. */
  key(cursor: Point, button = 0): void {
    const s = this.stroke;
    if (s?.keyboard) {
      this.move(cursor);
      return this.end();
    }
    this.begin(cursor, button, true);
    if (this.tools.tool === 'pencil') this.end();
  }

  /** The shape being dragged, for the preview. */
  preview(): { points: Point[]; ch: string } | null {
    const s = this.stroke;
    if (!s || !isShapeTool(s.tool)) return null;
    // Only one inbox survives a shape of them: show just that one.
    return { points: s.ch === 'P' ? [s.last] : shapePoints(s.tool, s.anchor, s.last), ch: s.ch };
  }
}
